const _jsxFileName = '';
import React, { useState } from 'react';
import { X, Percent, Tag, Calendar, DollarSign, AlertCircle } from 'lucide-react';

const AdminPromoModal = ({ isOpen, onClose, selectedProducts, onApplyPromo }) => {
  const [promoType, setPromoType] = useState('percentage');
  const [promoValue, setPromoValue] = useState(0);
  const [promoLabel, setPromoLabel] = useState('SALE');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoError, setPromoError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPromoError(null);

    if (promoType === 'percentage' && (promoValue <= 0 || promoValue > 100)) {
      setPromoError('Discount percentage must be between 1 and 100.');
      return;
    }
    if (promoType === 'fixed' && promoValue <= 0) {
      setPromoError('Promo price must be greater than ₱0.');
      return;
    }

    setIsSubmitting(true);

    try {
      const promoData = {
        product_ids: selectedProducts.map((p) => p.id),
        promo_label: promoLabel,
        promo_start_date: startDate || null,
        promo_end_date: endDate || null,
        promo_type: promoType,
        ...(promoType === 'percentage'
          ? { promo_percentage: promoValue, promo_price: null }
          : { promo_price: promoValue, promo_percentage: null }),
      };

      await onApplyPromo(promoData);
      onClose();
    } catch (error) {
      setPromoError(error.message || 'Failed to apply promo. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return React.createElement(
    'div',
    {
      className: 'fixed inset-0 z-[200] flex items-center justify-center',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 53 },
    },
    React.createElement('div', {
      className: 'absolute inset-0 bg-stone-900/60',
      onClick: onClose,
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 54 },
    }),

    React.createElement(
      'div',
      {
        className: 'relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-in',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 56 },
      },
      React.createElement(
        'div',
        {
          className:
            'px-6 py-4 border-b border-stone-100 flex justify-between items-center bg-stone-50',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 57 },
        },
        React.createElement(
          'h3',
          {
            className: 'font-serif text-xl text-stone-900 flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 58 },
          },
          React.createElement(Tag, {
            size: 18,
            className: 'text-gold-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 59 },
          }),
          'Apply Promo to ',
          selectedProducts.length,
          ' Product',
          selectedProducts.length !== 1 ? 's' : ''
        ),
        React.createElement(
          'button',
          {
            onClick: onClose,
            className: 'text-stone-400 hover:text-stone-900',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 62 },
          },
          React.createElement(X, {
            size: 20,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 63 },
          })
        )
      ),

      React.createElement(
        'form',
        {
          onSubmit: handleSubmit,
          className: 'p-6 space-y-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 67 },
        },
        /* Selected Products Preview */
        React.createElement(
          'div',
          {
            className: 'bg-stone-50 p-3 rounded-sm max-h-32 overflow-y-auto',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 69 },
          },
          React.createElement(
            'p',
            {
              className: 'text-xs font-medium text-stone-600 mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 70 },
            },
            'Selected Products:'
          ),
          React.createElement(
            'div',
            {
              className: 'space-y-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 71 },
            },
            selectedProducts
              .slice(0, 5)
              .map((p) =>
                React.createElement(
                  'div',
                  {
                    key: p.id,
                    className: 'text-xs text-stone-500 flex items-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 73 },
                  },
                  React.createElement('span', {
                    className: 'w-1.5 h-1.5 bg-gold-600 rounded-full',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 74 },
                  }),
                  React.createElement(
                    'span',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 75 } },
                    p.name,
                    ' - ₱',
                    p.price.toLocaleString()
                  )
                )
              ),
            selectedProducts.length > 5 &&
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 79 },
                },
                '...and ',
                selectedProducts.length - 5,
                ' more'
              )
          )
        ),

        /* Promo Label */
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 85 } },
          React.createElement(
            'label',
            {
              className: 'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 86 },
            },
            'Promo Label'
          ),
          React.createElement('input', {
            type: 'text',
            value: promoLabel,
            onChange: (e) => setPromoLabel(e.target.value),
            className:
              'w-full border border-stone-300 p-3 rounded-sm focus:border-gold-500 outline-none',
            placeholder: 'e.g. SALE, SUMMER SALE, FLASH SALE',
            required: true,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 89 },
          })
        ),

        /* Promo Type Selection */
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 100 } },
          React.createElement(
            'label',
            {
              className: 'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 101 },
            },
            'Promo Type'
          ),
          React.createElement(
            'div',
            {
              className: 'flex gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 104 },
            },
            React.createElement(
              'label',
              {
                className: 'flex items-center gap-2 cursor-pointer',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 105 },
              },
              React.createElement('input', {
                type: 'radio',
                checked: promoType === 'percentage',
                onChange: () => setPromoType('percentage'),
                className: 'w-4 h-4 accent-gold-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 106 },
              }),
              React.createElement(Percent, {
                size: 16,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 112 },
              }),
              React.createElement(
                'span',
                {
                  className: 'text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 113 },
                },
                'Percentage Off'
              )
            ),
            React.createElement(
              'label',
              {
                className: 'flex items-center gap-2 cursor-pointer',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 115 },
              },
              React.createElement('input', {
                type: 'radio',
                checked: promoType === 'fixed',
                onChange: () => setPromoType('fixed'),
                className: 'w-4 h-4 accent-gold-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 116 },
              }),
              React.createElement(DollarSign, {
                size: 16,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 122 },
              }),
              React.createElement(
                'span',
                {
                  className: 'text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 123 },
                },
                'Fixed Price'
              )
            )
          )
        ),

        /* Promo Value */
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 129 } },
          React.createElement(
            'label',
            {
              className: 'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 130 },
            },
            promoType === 'percentage' ? 'Discount Percentage' : 'Promo Price (₱)'
          ),
          React.createElement('input', {
            type: 'number',
            value: promoValue,
            onChange: (e) => setPromoValue(parseFloat(e.target.value) || 0),
            className:
              'w-full border border-stone-300 p-3 rounded-sm focus:border-gold-500 outline-none',
            min: '0',
            max: promoType === 'percentage' ? 100 : undefined,
            step: promoType === 'percentage' ? 1 : 0.01,
            required: true,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 133 },
          })
        ),

        /* Date Range */
        React.createElement(
          'div',
          {
            className: 'grid grid-cols-2 gap-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 146 },
          },
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 147 } },
            React.createElement(
              'label',
              {
                className: 'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 148 },
              },
              'Start Date'
            ),
            React.createElement(
              'div',
              {
                className: 'relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 151 },
              },
              React.createElement(Calendar, {
                size: 16,
                className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 152 },
              }),
              React.createElement('input', {
                type: 'date',
                value: startDate,
                onChange: (e) => setStartDate(e.target.value),
                className:
                  'w-full pl-10 pr-3 py-3 border border-stone-300 rounded-sm focus:border-gold-500 outline-none',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 153 },
              })
            )
          ),
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 161 } },
            React.createElement(
              'label',
              {
                className: 'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 162 },
              },
              'End Date'
            ),
            React.createElement(
              'div',
              {
                className: 'relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 165 },
              },
              React.createElement(Calendar, {
                size: 16,
                className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 166 },
              }),
              React.createElement('input', {
                type: 'date',
                value: endDate,
                onChange: (e) => setEndDate(e.target.value),
                className:
                  'w-full pl-10 pr-3 py-3 border border-stone-300 rounded-sm focus:border-gold-500 outline-none',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 167 },
              })
            )
          )
        ),

        /* Info Box */
        React.createElement(
          'div',
          {
            className: 'bg-blue-50 border border-blue-200 p-3 rounded-sm flex items-start gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 178 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            className: 'text-blue-600 flex-shrink-0 mt-0.5',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 179 },
          }),
          React.createElement(
            'p',
            {
              className: 'text-xs text-blue-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 180 },
            },
            'Promo will be applied to all selected products. If both price and end date are set, the promo will automatically expire on the end date.'
          )
        ),

        /* Error */
        promoError &&
          React.createElement(
            'div',
            {
              className:
                'bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm flex items-center justify-between',
            },
            React.createElement('span', null, promoError),
            React.createElement(
              'button',
              {
                onClick: () => setPromoError(null),
                className: 'ml-3 text-red-400 hover:text-red-700 text-lg leading-none',
              },
              '×'
            )
          ),

        /* Actions */
        React.createElement(
          'div',
          {
            className: 'flex justify-end gap-3 pt-4 border-t border-stone-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 187 },
          },
          React.createElement(
            'button',
            {
              type: 'button',
              onClick: onClose,
              className:
                'px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 188 },
            },
            'Cancel'
          ),
          React.createElement(
            'button',
            {
              type: 'submit',
              disabled: isSubmitting || selectedProducts.length === 0,
              className:
                'px-6 py-2 bg-gold-600 text-white text-xs uppercase tracking-widest hover:bg-gold-700 disabled:opacity-50 flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 195 },
            },

            isSubmitting
              ? React.createElement(
                  React.Fragment,
                  null,
                  React.createElement('div', {
                    className:
                      'w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 202 },
                  }),
                  'Applying...'
                )
              : React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(Tag, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 207 },
                  }),
                  ' Apply Promo'
                )
          )
        )
      )
    )
  );
};

export default AdminPromoModal;
